// @ts-strict-ignore
import isEqual from 'lodash/isEqual';

import {getActiveElement} from 'common/util/focusable';

export type ModifierKeys = {[x in keyof typeof KeyModifiers]: boolean};

// for KeyboardEvent.key (https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key) (KeyboardEvent.keyCode is deprecated)
export enum KeyModifiers {
  Cmd = `metaKey`,
  Ctrl = `ctrlKey`,
  Alt = `altKey`,
  Shift = `shiftKey`,
}

/**
 * Returns true if Mac, false otherwise
 */
export const isMacOS = () => {
  return window.navigator.appVersion.includes(`Mac`);
};

/**
 * Returns the commandKey based on the operating system we are using
 */
export const getCommandKey = () => {
  const isMac = isMacOS();

  return isMac ? KeyModifiers.Cmd : KeyModifiers.Ctrl;
};

export const getModifierLabel = (modifier: KeyModifiers) => {
  const modifiersLabels: {[key in KeyModifiers]: string} = {
    [KeyModifiers.Cmd]: `⌘`,
    [KeyModifiers.Shift]: isMacOS() ? `⇧` : `Shift`,
    [KeyModifiers.Alt]: isMacOS() ? `⌥` : `Alt`,
    [KeyModifiers.Ctrl]: isMacOS() ? `^` : `Ctrl`,
  };
  return modifiersLabels[modifier];
};

/**
 * Generates a keyboard shortcut label
 */
export function generateShortcutLabel(
  key: string,
  {modifiers = [], forMarkdown, joinStr}: {modifiers?: KeyModifiers[]; forMarkdown?: boolean; joinStr?: string} = {},
) {
  const join = joinStr ?? (forMarkdown ? ` ` : ` + `);

  const parts = [...modifiers.map((modifier) => getModifierLabel(modifier)), key];

  if (forMarkdown) {
    return parts
      .map((part) => {
        const updatedPart =
          part === `\``
            ? // backtick needs to be escaped in markdown
              `\`\` ${part} \`\``
            : part;

        return `\`${updatedPart}\``;
      })
      .join(join);
  }

  return parts.join(join);
}

/**
 * @param {{[string]: Function}} keyHandlerMap key code definition to handler function map
 * @param {Object} options
 * @param {boolean} options.stopPropagation stops event bubbling
 *
 * @example
 *  document.addEventListener(`keydown`, ev => keyEventSwitcher(ev, {
 *    'Escape': handleEscape,
 *    [`${KeyModifiers.Alt}` + KeyK`]: handleSearch,
 *    'ArrowLeft': handleArrowLeft,
 *    'ArrowRight': handleArrowRight,
 *  })
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/code#Code_values
 */
export function keyEventSwitcher(
  keyHandlerMap: {[key: string]: (ev?: KeyboardEvent) => void},
  {stopPropagation = false, usableInInputs = false} = {},
) {
  // constructs mapping of key combinations to a handler function
  const keyCodeMap = new Map<Set<string>, (ev: KeyboardEvent) => void>();
  for (const [code, fn] of Object.entries(keyHandlerMap)) {
    const codeParts = code.replace(/ /g, ``).split(`+`);
    const key = new Set<string>(codeParts);
    keyCodeMap.set(key, fn);
  }

  return function keyEventHandler(ev: KeyboardEvent) {
    if (!usableInInputs) {
      const tagName = getActiveElement()?.tagName.toLowerCase();
      if (tagName === `input` || tagName === `textarea`) {
        return;
      }
    }

    const keyCodeParts = new Set<string>([ev.code]);
    const modifiers = getModifierKeys(ev);
    for (const [modifierName, modifierActive] of Object.entries(modifiers)) {
      if (modifierActive) {
        keyCodeParts.add(KeyModifiers[modifierName]);
      }
    }

    // find a matching hanlder
    let handlerFn: ((ev?: KeyboardEvent) => void) | null = null;
    for (const [key, fn] of keyCodeMap) {
      if (isEqual(key, keyCodeParts)) {
        handlerFn = fn;
        break;
      }
    }

    if (handlerFn) {
      if (stopPropagation) {
        ev.stopPropagation();
        ev.preventDefault();
      }

      handlerFn(ev);
    }
  };
}

export function getModifierKeys(ev: MouseEvent | KeyboardEvent): ModifierKeys {
  return {
    Alt: ev[KeyModifiers.Alt],
    Ctrl: ev[KeyModifiers.Ctrl],
    Shift: ev[KeyModifiers.Shift],
    Cmd: ev[KeyModifiers.Cmd],
  };
}
